import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Info } from "lucide-react";
import { useVariableMappings } from "@/hooks/useVariableMappings";
import {
  ExtractedVariable,
  VariableMapping,
  SourceType,
  VariableMappingFormData,
  ClientFieldType,
  StaticType,
  DateFormatType,
} from "@/types/variable-mappings";
import { QuestionSelector } from "./QuestionSelector";
import { CalculationSelector } from "./CalculationSelector";
import { ClientFieldSelector } from "./ClientFieldSelector";
import { StaticFieldSelector } from "./StaticFieldSelector";
import { MatrixRowSelector } from "@/components/MatrixRowSelector";
import { HierarchicalGroupSelector } from "./HierarchicalGroupSelector";
import { useFormQuestionsData } from "@/hooks/useFormRelationships";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";

interface VariableMappingFormProps {
  templateId: number;
  variable: ExtractedVariable;
  existingMapping?: VariableMapping;
  onSuccess: () => void;
  onCancel: () => void;
  jotformFormId?: string | null;
  jotformFormIdReferral?: string | null;
}

export function VariableMappingForm({
  templateId,
  variable,
  existingMapping,
  onSuccess,
  onCancel,
  jotformFormId,
  jotformFormIdReferral,
}: VariableMappingFormProps) {
  const { saveMapping, isSaving } = useVariableMappings(templateId);

  const [sourceType, setSourceType] = useState<SourceType>(
    existingMapping?.source_type || 'question'
  );
  const [description, setDescription] = useState(existingMapping?.description || '');
  const [defaultValue, setDefaultValue] = useState(existingMapping?.default_value || '');

  // Question fields
  const [questionId, setQuestionId] = useState<number | undefined>(
    existingMapping?.question_id
  );
  const [questionName, setQuestionName] = useState<string | undefined>(
    existingMapping?.question_name
  );
  const [questionGroup, setQuestionGroup] = useState<'mentor' | 'manager' | 'equipo' | undefined>(
    existingMapping?.question_group
  );
  const [formSource, setFormSource] = useState<'main' | 'referral'>(
    existingMapping?.form_source === 'referral' ? 'referral' : 'main'
  );

  // Matrix fields
  const [matrixRowId, setMatrixRowId] = useState<string | undefined>(
    existingMapping?.matrix_row_id
  );
  const [matrixRowText, setMatrixRowText] = useState<string | undefined>(
    existingMapping?.matrix_row_text
  );
  const [isMatrixQuestion, setIsMatrixQuestion] = useState(false);

  // Use the same logic as ChartDataParams
  const mainFormData = useFormQuestionsData(jotformFormId);
  const referralFormData = useFormQuestionsData(jotformFormIdReferral);

  // Check if selected question is a matrix question
  useEffect(() => {
    const checkMatrixQuestion = async () => {
      console.log('🔍 [MATRIX CHECK] Verificando pregunta de matriz:', {
        questionName,
        formSource,
        mainFormData: mainFormData.formsData,
        referralFormData: referralFormData.formsData
      });

      if (!questionName) {
        console.log('❌ [MATRIX CHECK] No hay pregunta seleccionada');
        setIsMatrixQuestion(false);
        return;
      }

      // Get the correct form data based on formSource
      const currentFormData = formSource === 'main' ? mainFormData : referralFormData;
      const formsData = currentFormData.formsData || [];
      
      console.log('🔍 [MATRIX CHECK] Datos del formulario actual:', {
        formSource,
        formsDataLength: formsData.length,
        formsData: formsData.map(f => ({ formId: f.formId, isReferral: f.isReferral, questionsCount: f.questions?.length }))
      });
      
      // Find the form that matches our formSource
      const targetForm = formsData.find(form => 
        formSource === 'main' ? !form.isReferral : form.isReferral
      );

      console.log('🔍 [MATRIX CHECK] Formulario objetivo encontrado:', {
        targetForm: targetForm ? { formId: targetForm.formId, isReferral: targetForm.isReferral, questionsCount: targetForm.questions?.length } : null
      });

      if (!targetForm || !targetForm.questions) {
        console.log('❌ [MATRIX CHECK] No se encontró formulario objetivo o no tiene preguntas');
        setIsMatrixQuestion(false);
        return;
      }

      // Find the question in the form
      const question = targetForm.questions.find(
        (q: any) => q.name === questionName || q.qid === questionName
      );

      console.log('🔍 [MATRIX CHECK] Pregunta encontrada:', {
        question: question ? { name: question.name, qid: question.qid, type: question.type } : null,
        isMatrix: question?.type === 'control_matrix'
      });

      setIsMatrixQuestion(question?.type === 'control_matrix');
    };

    checkMatrixQuestion();
  }, [questionName, formSource, mainFormData, referralFormData]);

  // Calculation fields
  const [calculationType, setCalculationType] = useState<string | undefined>(
    existingMapping?.calculation_type
  );
  const [calculationConfig, setCalculationConfig] = useState<any>(
    existingMapping?.calculation_config
  );

  // Client fields - extraer de calculation_config si existe
  // NOTA: El cliente se resuelve automáticamente: submission → launch → client
  const [selectedClientField, setSelectedClientField] = useState<ClientFieldType | undefined>(
    existingMapping?.source_type === 'client' && existingMapping?.calculation_config
      ? (existingMapping.calculation_config as any)?.client_field
      : undefined
  );

  // Static fields - extraer de calculation_config si existe
  const [selectedStaticType, setSelectedStaticType] = useState<StaticType | undefined>(
    existingMapping?.source_type === 'static' && existingMapping?.calculation_config
      ? (existingMapping.calculation_config as any)?.static_type
      : undefined
  );
  const [selectedDateFormat, setSelectedDateFormat] = useState<DateFormatType>(
    existingMapping?.source_type === 'static' && existingMapping?.calculation_config
      ? (existingMapping.calculation_config as any)?.format || 'MMMM YYYY'
      : 'MMMM YYYY'
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const formData: VariableMappingFormData = {
      variable_name: variable.name,
      variable_placeholder: variable.placeholder,
      source_type: sourceType,
      description: description || undefined,
      default_value: defaultValue || undefined,
    };

    if (sourceType === 'question') {
      if (!questionName) {
        alert('Por favor selecciona una pregunta');
        return;
      }

      // Validate matrix question
      if (isMatrixQuestion && !matrixRowId) {
        alert('Por favor selecciona una fila de la matriz');
        return;
      }

      // Validate form source when referral form is available
      if (jotformFormIdReferral && !formSource) {
        alert('Por favor selecciona el origen del formulario');
        return;
      }

      // En V2 no usamos question_id, solo question_name
      formData.question_id = null;
      formData.question_name = questionName;
      formData.question_group = questionGroup;
      formData.form_source = formSource;

      // Add matrix fields if applicable
      if (isMatrixQuestion && matrixRowId) {
        formData.matrix_row_id = matrixRowId;
        formData.matrix_row_text = matrixRowText;
      }
    } else if (sourceType === 'calculation') {
      if (!calculationType || !calculationConfig) {
        alert('Por favor configura el cálculo');
        return;
      }
      formData.calculation_type = calculationType as any;
      formData.calculation_config = calculationConfig;
    } else if (sourceType === 'client') {
      if (!selectedClientField) {
        alert('Por favor selecciona un campo del cliente');
        return;
      }
      // Almacenar solo client_field en calculation_config
      // NOTA: El cliente se resuelve automáticamente: submission → launch → client
      formData.calculation_config = {
        client_field: selectedClientField
      };
    } else if (sourceType === 'static') {
      if (!selectedStaticType) {
        alert('Por favor selecciona un tipo de valor');
        return;
      }
      // Almacenar static_type y format en calculation_config
      formData.calculation_config = {
        static_type: selectedStaticType,
        format: selectedDateFormat
      };
    }

    try {
      await saveMapping({
        id: existingMapping?.id,
        formData,
      });
      onSuccess();
    } catch (error) {
      console.error('Error saving mapping:', error);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Variable Info */}
      <div className="bg-muted p-4 rounded-lg">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="font-medium">Variable:</span> {variable.name}
          </div>
          <div>
            <span className="font-medium">Apariciones:</span> {variable.occurrences}
          </div>
        </div>
      </div>

      {/* Source Type */}
      <div className="space-y-3">
        <Label>Tipo de Fuente</Label>
        <RadioGroup value={sourceType} onValueChange={(v) => setSourceType(v as SourceType)}>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="question" id="type-question" />
            <Label htmlFor="type-question" className="font-normal cursor-pointer">
              Pregunta Directa - Tomar el valor de una respuesta específica
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="calculation" id="type-calculation" />
            <Label htmlFor="type-calculation" className="font-normal cursor-pointer">
              Cálculo - Computar un valor usando operaciones matemáticas
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="client" id="type-client" />
            <Label htmlFor="type-client" className="font-normal cursor-pointer">
              Datos del Cliente - Usar informacion del cliente (nombre, logo, mision)
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="static" id="type-static" />
            <Label htmlFor="type-static" className="font-normal cursor-pointer">
              Valor Automatico - Generar valor al exportar (fecha actual, etc.)
            </Label>
          </div>
        </RadioGroup>
      </div>

      {/* Question Selector */}
      {sourceType === 'question' && (
        <div className="space-y-4">
          {/* ROW 1: Form Source Selector (2 columns grid) - Only show when referral form exists */}
          {jotformFormIdReferral && (
            <Card className="border-purple-200 bg-gradient-to-br from-purple-50/50 to-pink-50/30">
              <CardHeader className="pb-3">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                    <Info className="h-4 w-4 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <CardTitle className="text-purple-900 text-base">Origen del Formulario</CardTitle>
                    <CardDescription className="text-purple-700 mt-1">
                      Selecciona si esta variable proviene del formulario principal o del formulario de referidos
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <RadioGroup value={formSource} onValueChange={(v) => setFormSource(v as 'main' | 'referral')}>
                  <div className="flex items-center space-x-2 p-3 rounded-lg hover:bg-purple-50/50 transition-colors">
                    <RadioGroupItem value="main" id="form-source-main" />
                    <Label htmlFor="form-source-main" className="font-normal cursor-pointer flex-1">
                      <div className="font-medium">Formulario Principal</div>
                      <div className="text-xs text-muted-foreground">Respuestas del usuario principal</div>
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2 p-3 rounded-lg hover:bg-purple-50/50 transition-colors">
                    <RadioGroupItem value="referral" id="form-source-referral" />
                    <Label htmlFor="form-source-referral" className="font-normal cursor-pointer flex-1">
                      <div className="font-medium">Formulario de Referidos</div>
                      <div className="text-xs text-muted-foreground">Respuestas de los referidos</div>
                    </Label>
                  </div>
                </RadioGroup>
              </CardContent>
            </Card>
          )}

          {/* ROW 2: Group and Question Selector (2 columns grid) - INDEPENDENT ROW */}
          <div className="grid grid-cols-2 gap-4">
            <HierarchicalGroupSelector
              selectedGroup={questionGroup || ''}
              selectedQuestion={questionName || ''}
              onGroupChange={(group) => setQuestionGroup(group as 'mentor' | 'manager' | 'equipo')}
              onQuestionChange={(questionKey, questionText) => {
                console.log('🔄 Pregunta cambiada:', { questionKey, questionText });
                setQuestionId(undefined);
                setQuestionName(questionKey);
                // Reset matrix selection when question changes
                setMatrixRowId(undefined);
                setMatrixRowText(undefined);
              }}
              jotformFormId={formSource === 'main' ? jotformFormId : jotformFormIdReferral}
              showQuestionSelector={true}
              questionSelectorType="select"
            />
          </div>

          {/* ROW 3: Matrix Row Selection - Full width - INDEPENDENT ROW */}
          {isMatrixQuestion && questionName && (
            <>
              {console.log('🔍 [MATRIX RENDER] Estado de matriz:', { isMatrixQuestion, questionName })}
              <MatrixRowSelector
                questionName={questionName}
                jotformFormId={formSource === 'main' ? jotformFormId || '' : jotformFormIdReferral || ''}
                selectedRowId={matrixRowId}
                onRowChange={(rowId, rowText) => {
                  console.log('✅ Fila de matriz seleccionada:', { rowId, rowText });
                  setMatrixRowId(rowId);
                  setMatrixRowText(rowText);
                }}
              />
            </>
          )}
        </div>
      )}

      {/* Calculation Selector */}
      {sourceType === 'calculation' && (
        <CalculationSelector
          templateId={templateId}
          selectedType={calculationType}
          config={calculationConfig}
          onTypeChange={setCalculationType}
          onConfigChange={setCalculationConfig}
        />
      )}

      {/* Client Selector */}
      {sourceType === 'client' && (
        <ClientFieldSelector
          selectedField={selectedClientField}
          onFieldChange={setSelectedClientField}
        />
      )}

      {/* Static Selector */}
      {sourceType === 'static' && (
        <StaticFieldSelector
          selectedType={selectedStaticType}
          selectedFormat={selectedDateFormat}
          onTypeChange={setSelectedStaticType}
          onFormatChange={setSelectedDateFormat}
        />
      )}

      {/* Description */}
      <div className="space-y-2">
        <Label htmlFor="description">Descripción (Opcional)</Label>
        <Textarea
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Ej: Nombre completo del mentor"
          rows={2}
        />
      </div>

      {/* Default Value */}
      <div className="space-y-2">
        <Label htmlFor="default">Valor por Defecto (Opcional)</Label>
        <Input
          id="default"
          value={defaultValue}
          onChange={(e) => setDefaultValue(e.target.value)}
          placeholder="Valor a usar si no se puede calcular/obtener"
        />
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSaving}>
          Cancelar
        </Button>
        <Button type="submit" disabled={isSaving}>
          {isSaving ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Guardando...
            </>
          ) : (
            'Guardar Mapeo'
          )}
        </Button>
      </div>
    </form>
  );
}
